import React from 'react'
import { useLocation } from 'react-router-dom'
import Heading from '@eversports/klimt-primitives/legacy/Heading'

import Icon, { Icons } from '@eversports/react-components/primitives/Icon'
import Text from '@eversports/klimt-primitives/legacy/Text'

import { motion } from '@eversports/react-components/design-tokens/motion'
import Box from '@eversports/klimt-primitives/legacy/Box'
import Stack from '@eversports/klimt-primitives/legacy/Stack'
import Venue from '../../typings/Venue'
import Customer from '../../typings/Customer'
import AppPromotion from '../AppPromotion'
import { DesktopBookingDetails, MobileBookingDetails } from '../BookingDetails'
import BottomFixedCollapsibleSection from '../BottomFixedCollapsibleSection'

import useCheckoutContext from '../../data/useCheckoutContext'
import getPhoenixUrlParams from '../../lib/get-phoenix-url-params'
import ConfirmationHeading from './ConfirmationHeading'
import ConfirmationSubHeading from './ConfirmationSubHeading'
import MobileConfirmationCollapsibleCardHeader from './MobileConfirmationCollapsibleCardHeader'
import NewsletterBanner from './NewsletterBanner'
import ConfirmationActions from './ConfirmationActions'

interface Props {
  venue: Venue
  eventId?: string
  isOrderConfirmed: boolean
  hasBooking: boolean
  hasSelfCheckIn: boolean
  invoiceDownloadLink?: string | null
  wasSomethingPayed: boolean
  voucherCode?: string
  customer: Customer | null
  isBookingRequest: boolean
}

const ConfirmationComponent: React.FC<React.PropsWithChildren<Props>> = ({
  venue,
  eventId,
  hasBooking,
  isOrderConfirmed,
  hasSelfCheckIn,
  invoiceDownloadLink,
  wasSomethingPayed,
  voucherCode,
  customer,
  isBookingRequest,
}) => {
  const location = useLocation()
  const { isVenueBrandingEnabled } = useCheckoutContext()
  const { hideEversportsBranding } = getPhoenixUrlParams(location.search)
  const isBrandingHidden = hideEversportsBranding || isVenueBrandingEnabled
  return (
    <Stack sx={{ marginX: 'auto', width: '100%', alignItems: 'center' }}>
      <Stack
        sx={{ justifyContent: 'center', width: '100%', maxWidth: '840px', padding: 2 }}
        data-testid="confirmation-page"
      >
        <motion.div
          initial={{ opacity: 0, y: '-100vh' }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ease: 'easeInOut' }}
        >
          <Stack sx={{ alignItems: 'center', gap: 3, marginY: 1 }}>
            <motion.span
              initial={{ scale: 0.25, opacity: 0 }}
              animate={{ scale: 1, opacity: 1, transition: { ease: 'easeInOut', duration: 0.5, delay: 0.25 } }}
            >
              <Icon icon={Icons.SUCCESS} size="xxx-large" color={isBrandingHidden ? 'text.primary' : 'primary.main'} />
            </motion.span>
            <Heading is="h1" variant="xlarge" sx={{ textAlign: 'center', paddingX: 3 }}>
              <ConfirmationHeading
                isOrderConfirmed={isOrderConfirmed}
                hasBooking={hasBooking}
                wasSomethingPayed={wasSomethingPayed}
                venueName={venue.name}
                isBookingRequest={isBookingRequest}
              />
            </Heading>
            <Box sx={{ maxWidth: { xs: '308px', sm: '550px' } }}>
              <ConfirmationSubHeading
                isOrderConfirmed={isOrderConfirmed}
                hasBooking={hasBooking}
                hasSelfCheckIn={hasSelfCheckIn}
                venueName={venue.name}
                isBookingRequest={isBookingRequest}
              />
            </Box>
          </Stack>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: '200vh' }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ease: 'easeInOut' }}
        >
          <Stack sx={{ gap: 3 }}>
            {!isBookingRequest && (
              <ConfirmationActions
                venue={venue}
                eventId={eventId}
                hasBooking={hasBooking}
                invoiceDownloadLink={invoiceDownloadLink}
                voucherCode={voucherCode}
              />
            )}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <DesktopBookingDetails />
            </Box>
            {customer && !customer.isSubscribedToNewsletter && (
              <NewsletterBanner venue={venue} customerId={customer.id} />
            )}
            {!isBrandingHidden && <AppPromotion venue={venue} hasSelfCheckIn={hasSelfCheckIn} />}
          </Stack>
        </motion.div>
      </Stack>
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <BottomFixedCollapsibleSection collapsibleContent={<MobileBookingDetails />} showExpandIcon hideHeaderOnExpand>
          <Text sx={{ fontWeight: 'bold', textAlign: 'center' }}>
            <MobileConfirmationCollapsibleCardHeader hasBooking={hasBooking} />
          </Text>
        </BottomFixedCollapsibleSection>
      </Box>
    </Stack>
  )
}
export default ConfirmationComponent
